/**
 * Auto-detects run configurations from project files.
 *
 * Framework-aware detection for 30+ project types. Detects monorepos (Nx, Turborepo),
 * JS/TS frameworks (Next.js, Angular, Vite, SvelteKit, Nuxt, etc.), Rust (Cargo, Tauri),
 * Python (Django, Flask, FastAPI, Poetry), .NET, Go, Java (Gradle, Maven), Ruby/Rails,
 * PHP/Laravel, Flutter, Deno, Zig, Swift, CMake, Docker Compose, Makefile, Helm, and more.
 */

import type { RunConfiguration } from './projectSettings';

// ─── Types & Interfaces ─────────────────────────────────────────────────────

export interface DetectionContext {
  readFile: (path: string) => Promise<string>;
  rootFiles: string[];
}

interface PackageJson {
  scripts?: Record<string, string>;
  dependencies?: Record<string, string>;
  devDependencies?: Record<string, string>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

let _counter = 0;

function uid(): string {
  return Date.now().toString(36) + (++_counter).toString(36);
}

function cfg(name: string, command: string): RunConfiguration {
  return { id: uid(), name, command };
}

function hasRootFile(ctx: DetectionContext, name: string): boolean {
  return ctx.rootFiles.includes(name);
}

function hasAnyRootFile(ctx: DetectionContext, names: string[]): boolean {
  return names.some((n) => ctx.rootFiles.includes(n));
}

async function readJsonSafe<T = unknown>(ctx: DetectionContext, path: string): Promise<T | null> {
  try {
    const content = await ctx.readFile(path);
    return JSON.parse(content) as T;
  } catch {
    return null;
  }
}

function hasDep(pkg: PackageJson, name: string): boolean {
  return !!(pkg.dependencies?.[name] || pkg.devDependencies?.[name]);
}

function hasAnyDep(pkg: PackageJson, names: string[]): boolean {
  return names.some((n) => hasDep(pkg, n));
}

/** Lifecycle hooks and pre/post scripts to skip when listing npm scripts. */
const LIFECYCLE_SCRIPTS = new Set([
  'prepare', 'prepublishOnly', 'prepack', 'postpack',
  'install', 'uninstall', 'preinstall', 'postinstall',
]);

function filterNpmScripts(scripts: Record<string, string>, claimedNames: Set<string>): RunConfiguration[] {
  const allNames = new Set(Object.keys(scripts));
  return Object.keys(scripts)
    .filter((name) => {
      if (LIFECYCLE_SCRIPTS.has(name)) return false;
      if (claimedNames.has(name)) return false;
      for (const prefix of ['pre', 'post']) {
        if (name.startsWith(prefix) && allNames.has(name.slice(prefix.length))) return false;
      }
      return true;
    })
    .map((name) => cfg(`npm: ${name}`, `npm run ${name}`));
}

// ─── Main Entry Point ────────────────────────────────────────────────────────

export async function detectRunConfigurations(ctx: DetectionContext): Promise<RunConfiguration[]> {
  // Parse package.json once — shared across all JS detectors
  const pkg: PackageJson | null = hasRootFile(ctx, 'package.json')
    ? await readJsonSafe<PackageJson>(ctx, 'package.json')
    : null;

  const detected = await Promise.all([
    // Monorepo detectors
    detectNx(ctx),
    detectTurborepo(ctx),
    // JS/TS framework detectors
    detectJsFrameworks(ctx, pkg),
    // Rust detectors
    detectRust(ctx),
    // Python detectors
    detectPython(ctx),
    // .NET detectors
    detectDotNet(ctx),
    // Go
    detectGo(ctx),
    // Java
    detectGradle(ctx),
    detectMaven(ctx),
    // Ruby
    detectRuby(ctx),
    // PHP
    detectPhp(ctx),
    // Flutter
    detectFlutter(ctx),
    // Deno
    detectDeno(ctx),
    // Zig
    detectZig(ctx),
    // Swift
    detectSwift(ctx),
    // CMake
    detectCMake(ctx),
    // Infrastructure
    detectDockerCompose(ctx),
    detectMakefile(ctx),
    detectHelm(ctx),
  ]);

  // Flatten and deduplicate by normalized command
  const seen = new Set<string>();
  const results: RunConfiguration[] = [];
  for (const config of detected.flat()) {
    const key = config.command.trim().toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      results.push(config);
    }
  }
  return results;
}

// ─── Monorepo Detectors ──────────────────────────────────────────────────────

async function detectNx(ctx: DetectionContext): Promise<RunConfiguration[]> {
  if (!hasRootFile(ctx, 'nx.json')) return [];
  return [
    cfg('Nx: Serve', 'npx nx serve'),
    cfg('Nx: Build', 'npx nx build'),
    cfg('Nx: Test', 'npx nx test'),
    cfg('Nx: Lint', 'npx nx lint'),
  ];
}

async function detectTurborepo(ctx: DetectionContext): Promise<RunConfiguration[]> {
  if (!hasRootFile(ctx, 'turbo.json')) return [];
  const turbo = await readJsonSafe<{ tasks?: Record<string, unknown>; pipeline?: Record<string, unknown> }>(ctx, 'turbo.json');
  if (!turbo) return [cfg('Turbo: Build', 'npx turbo build')];

  // turbo.json v2 uses "tasks", v1 uses "pipeline"
  const taskMap = turbo.tasks ?? turbo.pipeline;
  if (!taskMap || typeof taskMap !== 'object') return [cfg('Turbo: Build', 'npx turbo build')];

  return Object.keys(taskMap)
    .filter((name) => !name.startsWith('#') && !name.includes('#'))
    .map((name) => cfg(`Turbo: ${name}`, `npx turbo ${name}`));
}

// ─── JS/TS Framework Detectors ───────────────────────────────────────────────

async function detectJsFrameworks(_ctx: DetectionContext, pkg: PackageJson | null): Promise<RunConfiguration[]> {
  if (!pkg) return [];

  const configs: RunConfiguration[] = [];
  const claimedScripts = new Set<string>();

  // Helper to claim npm script names that the framework provides
  const claim = (...names: string[]) => names.forEach((n) => claimedScripts.add(n));

  // --- Next.js ---
  if (hasDep(pkg, 'next')) {
    configs.push(
      cfg('Next.js: Dev', 'npx next dev'),
      cfg('Next.js: Build', 'npx next build'),
      cfg('Next.js: Start', 'npx next start'),
      cfg('Next.js: Lint', 'npx next lint'),
    );
    claim('dev', 'build', 'start', 'lint');
  }

  // --- Angular ---
  if (hasDep(pkg, '@angular/core')) {
    configs.push(
      cfg('Angular: Serve', 'npx ng serve'),
      cfg('Angular: Build', 'npx ng build'),
      cfg('Angular: Test', 'npx ng test'),
      cfg('Angular: Lint', 'npx ng lint'),
    );
    claim('start', 'build', 'test', 'lint', 'serve', 'e2e');
  }

  // --- SvelteKit ---
  if (hasDep(pkg, '@sveltejs/kit')) {
    configs.push(
      cfg('SvelteKit: Dev', 'npm run dev'),
      cfg('SvelteKit: Build', 'npm run build'),
      cfg('SvelteKit: Preview', 'npm run preview'),
    );
    claim('dev', 'build', 'preview');
  }

  // --- Nuxt ---
  if (hasDep(pkg, 'nuxt')) {
    configs.push(
      cfg('Nuxt: Dev', 'npx nuxi dev'),
      cfg('Nuxt: Build', 'npx nuxi build'),
      cfg('Nuxt: Preview', 'npx nuxi preview'),
    );
    claim('dev', 'build', 'preview', 'generate');
  }

  // --- Remix ---
  if (hasDep(pkg, '@remix-run/dev')) {
    configs.push(
      cfg('Remix: Dev', 'npx remix dev'),
      cfg('Remix: Build', 'npx remix build'),
    );
    claim('dev', 'build', 'start');
  }

  // --- Astro ---
  if (hasDep(pkg, 'astro')) {
    configs.push(
      cfg('Astro: Dev', 'npx astro dev'),
      cfg('Astro: Build', 'npx astro build'),
      cfg('Astro: Preview', 'npx astro preview'),
    );
    claim('dev', 'build', 'preview');
  }

  // --- Gatsby ---
  if (hasDep(pkg, 'gatsby')) {
    configs.push(
      cfg('Gatsby: Develop', 'npx gatsby develop'),
      cfg('Gatsby: Build', 'npx gatsby build'),
      cfg('Gatsby: Serve', 'npx gatsby serve'),
    );
    claim('develop', 'build', 'serve', 'start');
  }

  // --- Docusaurus ---
  if (hasDep(pkg, '@docusaurus/core')) {
    configs.push(
      cfg('Docusaurus: Start', 'npx docusaurus start'),
      cfg('Docusaurus: Build', 'npx docusaurus build'),
    );
    claim('start', 'build', 'serve', 'deploy');
  }

  // --- Storybook ---
  if (hasDep(pkg, 'storybook') || hasDep(pkg, '@storybook/react')) {
    configs.push(
      cfg('Storybook: Dev', 'npx storybook dev'),
      cfg('Storybook: Build', 'npx storybook build'),
    );
    claim('storybook', 'build-storybook');
  }

  // --- Electron ---
  if (hasDep(pkg, 'electron')) {
    if (pkg.scripts?.start) {
      configs.push(cfg('Electron: Start', 'npm start'));
    } else {
      configs.push(cfg('Electron: Start', 'npx electron .'));
    }
    claim('start');
  }

  // --- Expo ---
  if (hasDep(pkg, 'expo')) {
    configs.push(
      cfg('Expo: Start', 'npx expo start'),
      cfg('Expo: Android', 'npx expo run:android'),
      cfg('Expo: iOS', 'npx expo run:ios'),
    );
    claim('start', 'android', 'ios', 'web');
  }

  // --- React Native (without Expo) ---
  if (hasDep(pkg, 'react-native') && !hasDep(pkg, 'expo')) {
    configs.push(
      cfg('RN: Start', 'npx react-native start'),
      cfg('RN: Android', 'npx react-native run-android'),
      cfg('RN: iOS', 'npx react-native run-ios'),
    );
    claim('start', 'android', 'ios');
  }

  // --- NestJS ---
  if (hasDep(pkg, '@nestjs/core')) {
    configs.push(
      cfg('NestJS: Dev', 'npx nest start --watch'),
      cfg('NestJS: Build', 'npx nest build'),
    );
    claim('start', 'start:dev', 'start:debug', 'start:prod', 'build');
  }

  // --- Express/Fastify (only if not NestJS) ---
  if (!hasDep(pkg, '@nestjs/core') && hasAnyDep(pkg, ['express', 'fastify'])) {
    if (pkg.scripts?.dev) {
      configs.push(cfg('Server: Dev', 'npm run dev'));
      claim('dev');
    }
    if (pkg.scripts?.start) {
      configs.push(cfg('Server: Start', 'npm run start'));
      claim('start');
    }
  }

  // --- React CRA ---
  if (hasDep(pkg, 'react-scripts')) {
    configs.push(
      cfg('CRA: Start', 'npx react-scripts start'),
      cfg('CRA: Build', 'npx react-scripts build'),
      cfg('CRA: Test', 'npx react-scripts test'),
    );
    claim('start', 'build', 'test', 'eject');
  }

  // --- Vue CLI ---
  if (hasDep(pkg, '@vue/cli-service')) {
    configs.push(
      cfg('Vue CLI: Serve', 'npx vue-cli-service serve'),
      cfg('Vue CLI: Build', 'npx vue-cli-service build'),
    );
    claim('serve', 'build', 'lint');
  }

  // --- Vite (only if not already handled by Next, Angular, Svelte, etc.) ---
  if (
    hasDep(pkg, 'vite') &&
    !hasDep(pkg, 'next') &&
    !hasDep(pkg, '@angular/core') &&
    !hasDep(pkg, '@sveltejs/kit') &&
    !hasDep(pkg, 'nuxt') &&
    !hasDep(pkg, 'astro') &&
    !hasDep(pkg, '@remix-run/dev')
  ) {
    configs.push(
      cfg('Vite: Dev', 'npx vite'),
      cfg('Vite: Build', 'npx vite build'),
      cfg('Vite: Preview', 'npx vite preview'),
    );
    claim('dev', 'build', 'preview');
  }

  // --- Tauri (JS side) ---
  if (hasDep(pkg, '@tauri-apps/cli')) {
    configs.push(
      cfg('Tauri: Dev', 'npx tauri dev'),
      cfg('Tauri: Build', 'npx tauri build'),
    );
    claim('tauri', 'tauri:dev', 'tauri:build');
  }

  // --- npm script fallback: remaining unclaimed scripts ---
  if (pkg.scripts) {
    const remaining = filterNpmScripts(pkg.scripts, claimedScripts);
    configs.push(...remaining);
  }

  return configs;
}

// ─── Rust Detectors ──────────────────────────────────────────────────────────

async function detectRust(ctx: DetectionContext): Promise<RunConfiguration[]> {
  if (!hasRootFile(ctx, 'Cargo.toml')) return [];

  const content = await readSafe(ctx, 'Cargo.toml');
  if (!content) return [];

  const configs: RunConfiguration[] = [];

  // Check for workspace
  if (content.includes('[workspace]')) {
    configs.push(
      cfg('Cargo: Build Workspace', 'cargo build --workspace'),
      cfg('Cargo: Test Workspace', 'cargo test --workspace'),
    );
  }

  // Check for Tauri dependency
  if (content.includes('tauri')) {
    configs.push(
      cfg('Tauri: Dev', 'cargo tauri dev'),
      cfg('Tauri: Build', 'cargo tauri build'),
    );
  }

  // Check for binary vs library — look for [[bin]] section or absence of [lib]
  const hasBinSection = content.includes('[[bin]]') || content.includes('[lib]') === false;

  if (!content.includes('[workspace]')) {
    if (hasBinSection && !content.match(/\[lib\]\s*$/m)) {
      configs.push(
        cfg('Cargo: Run', 'cargo run'),
        cfg('Cargo: Build', 'cargo build'),
        cfg('Cargo: Test', 'cargo test'),
        cfg('Cargo: Check', 'cargo check'),
      );
    } else {
      // Library crate
      configs.push(
        cfg('Cargo: Build', 'cargo build'),
        cfg('Cargo: Test', 'cargo test'),
        cfg('Cargo: Doc', 'cargo doc --open'),
      );
    }
  }

  return configs;
}

async function readSafe(ctx: DetectionContext, path: string): Promise<string | null> {
  try {
    return await ctx.readFile(path);
  } catch {
    return null;
  }
}

// ─── Python Detectors ────────────────────────────────────────────────────────

async function detectPython(ctx: DetectionContext): Promise<RunConfiguration[]> {
  const configs: RunConfiguration[] = [];

  // Django
  if (hasRootFile(ctx, 'manage.py')) {
    configs.push(
      cfg('Django: Run Server', 'python manage.py runserver'),
      cfg('Django: Migrate', 'python manage.py migrate'),
      cfg('Django: Test', 'python manage.py test'),
      cfg('Django: Shell', 'python manage.py shell'),
    );
    return configs; // Django is definitive — skip other Python checks
  }

  // Check pyproject.toml for Poetry, Flask, FastAPI
  if (hasRootFile(ctx, 'pyproject.toml')) {
    const content = await readSafe(ctx, 'pyproject.toml');
    if (content) {
      if (content.includes('[tool.poetry]')) {
        configs.push(
          cfg('Poetry: Install', 'poetry install'),
          cfg('Poetry: Test', 'poetry run pytest'),
        );
      }

      if (content.includes('fastapi') || content.includes('FastAPI')) {
        configs.push(cfg('FastAPI: Dev', 'uvicorn main:app --reload'));
      }

      if (content.includes('flask') || content.includes('Flask')) {
        configs.push(cfg('Flask: Run', 'flask run --debug'));
      }

      if (configs.length > 0) return configs;

      // Generic pyproject.toml
      configs.push(cfg('pip install', 'pip install -e .'));
      if (content.includes('pytest')) {
        configs.push(cfg('pytest', 'pytest'));
      }
      return configs;
    }
  }

  // Check requirements.txt for Flask, FastAPI
  if (hasRootFile(ctx, 'requirements.txt')) {
    const content = await readSafe(ctx, 'requirements.txt');
    if (content) {
      if (content.includes('fastapi') || content.includes('FastAPI')) {
        configs.push(cfg('FastAPI: Dev', 'uvicorn main:app --reload'));
      }
      if (content.includes('flask') || content.includes('Flask')) {
        configs.push(cfg('Flask: Run', 'flask run --debug'));
      }
    }
    configs.push(cfg('pip install', 'pip install -r requirements.txt'));
    if (!configs.some((c) => c.command.includes('pytest'))) {
      configs.push(cfg('pytest', 'pytest'));
    }
    return configs;
  }

  return configs;
}

// ─── .NET Detectors ──────────────────────────────────────────────────────────

async function detectDotNet(ctx: DetectionContext): Promise<RunConfiguration[]> {
  const hasSln = ctx.rootFiles.some((f) => f.endsWith('.sln'));
  const hasCsproj = ctx.rootFiles.some((f) => f.endsWith('.csproj'));
  if (!hasSln && !hasCsproj) return [];

  return [
    cfg('.NET: Run', 'dotnet run'),
    cfg('.NET: Build', 'dotnet build'),
    cfg('.NET: Test', 'dotnet test'),
    cfg('.NET: Watch', 'dotnet watch run'),
  ];
}

// ─── Go ──────────────────────────────────────────────────────────────────────

async function detectGo(ctx: DetectionContext): Promise<RunConfiguration[]> {
  if (!hasRootFile(ctx, 'go.mod')) return [];
  return [
    cfg('Go: Run', 'go run .'),
    cfg('Go: Build', 'go build .'),
    cfg('Go: Test', 'go test ./...'),
    cfg('Go: Vet', 'go vet ./...'),
  ];
}

// ─── Java / Gradle ───────────────────────────────────────────────────────────

async function detectGradle(ctx: DetectionContext): Promise<RunConfiguration[]> {
  if (!hasAnyRootFile(ctx, ['build.gradle', 'build.gradle.kts'])) return [];

  const configs: RunConfiguration[] = [];
  const cmd = './gradlew';

  // Check for Spring Boot
  let isSpringBoot = false;
  for (const filename of ['build.gradle', 'build.gradle.kts']) {
    if (hasRootFile(ctx, filename)) {
      const content = await readSafe(ctx, filename);
      if (content && (content.includes('spring-boot') || content.includes('org.springframework.boot'))) {
        isSpringBoot = true;
      }
      break;
    }
  }

  configs.push(
    cfg('Gradle: Build', `${cmd} build`),
    cfg('Gradle: Test', `${cmd} test`),
  );

  if (isSpringBoot) {
    configs.push(cfg('Spring Boot: Run', `${cmd} bootRun`));
  } else {
    configs.push(cfg('Gradle: Run', `${cmd} run`));
  }

  return configs;
}

// ─── Java / Maven ────────────────────────────────────────────────────────────

async function detectMaven(ctx: DetectionContext): Promise<RunConfiguration[]> {
  if (!hasRootFile(ctx, 'pom.xml')) return [];

  const configs: RunConfiguration[] = [];
  const cmd = './mvnw';

  // Check for Spring Boot
  const content = await readSafe(ctx, 'pom.xml');
  const isSpringBoot = !!content && (content.includes('spring-boot') || content.includes('org.springframework.boot'));

  configs.push(
    cfg('Maven: Compile', `${cmd} compile`),
    cfg('Maven: Package', `${cmd} package`),
    cfg('Maven: Test', `${cmd} test`),
  );

  if (isSpringBoot) {
    configs.push(cfg('Spring Boot: Run', `${cmd} spring-boot:run`));
  }

  return configs;
}

// ─── Ruby / Rails ────────────────────────────────────────────────────────────

async function detectRuby(ctx: DetectionContext): Promise<RunConfiguration[]> {
  if (!hasRootFile(ctx, 'Gemfile')) return [];

  // Check for Rails
  if (hasRootFile(ctx, 'bin') && ctx.rootFiles.includes('config.ru')) {
    return [
      cfg('Rails: Server', 'bin/rails server'),
      cfg('Rails: Console', 'bin/rails console'),
      cfg('Rails: Test', 'bin/rails test'),
    ];
  }

  return [cfg('Bundle: Install', 'bundle install')];
}

// ─── PHP / Laravel ───────────────────────────────────────────────────────────

async function detectPhp(ctx: DetectionContext): Promise<RunConfiguration[]> {
  // Laravel
  if (hasRootFile(ctx, 'artisan')) {
    return [
      cfg('Laravel: Serve', 'php artisan serve'),
      cfg('Laravel: Migrate', 'php artisan migrate'),
      cfg('Laravel: Test', 'php artisan test'),
    ];
  }

  // Generic Composer
  if (hasRootFile(ctx, 'composer.json')) {
    const configs: RunConfiguration[] = [cfg('Composer: Install', 'composer install')];
    const composerJson = await readJsonSafe<{ scripts?: Record<string, unknown> }>(ctx, 'composer.json');
    if (composerJson?.scripts) {
      for (const name of Object.keys(composerJson.scripts)) {
        if (!name.startsWith('pre-') && !name.startsWith('post-')) {
          configs.push(cfg(`Composer: ${name}`, `composer run-script ${name}`));
        }
      }
    }
    return configs;
  }

  return [];
}

// ─── Flutter ─────────────────────────────────────────────────────────────────

async function detectFlutter(ctx: DetectionContext): Promise<RunConfiguration[]> {
  if (!hasRootFile(ctx, 'pubspec.yaml')) return [];
  return [
    cfg('Flutter: Run', 'flutter run'),
    cfg('Flutter: Build', 'flutter build apk'),
    cfg('Flutter: Test', 'flutter test'),
  ];
}

// ─── Deno ────────────────────────────────────────────────────────────────────

async function detectDeno(ctx: DetectionContext): Promise<RunConfiguration[]> {
  const denoConfig = hasRootFile(ctx, 'deno.json') ? 'deno.json'
    : hasRootFile(ctx, 'deno.jsonc') ? 'deno.jsonc'
    : null;
  if (!denoConfig) return [];

  const config = await readJsonSafe<{ tasks?: Record<string, string> }>(ctx, denoConfig);
  if (config?.tasks && Object.keys(config.tasks).length > 0) {
    return Object.keys(config.tasks).map((name) =>
      cfg(`Deno: ${name}`, `deno task ${name}`),
    );
  }

  return [cfg('Deno: Run', 'deno run main.ts')];
}

// ─── Zig ─────────────────────────────────────────────────────────────────────

async function detectZig(ctx: DetectionContext): Promise<RunConfiguration[]> {
  if (!hasRootFile(ctx, 'build.zig')) return [];
  return [
    cfg('Zig: Build', 'zig build'),
    cfg('Zig: Test', 'zig build test'),
    cfg('Zig: Run', 'zig build run'),
  ];
}

// ─── Swift ───────────────────────────────────────────────────────────────────

async function detectSwift(ctx: DetectionContext): Promise<RunConfiguration[]> {
  if (!hasRootFile(ctx, 'Package.swift')) return [];
  return [
    cfg('Swift: Build', 'swift build'),
    cfg('Swift: Run', 'swift run'),
    cfg('Swift: Test', 'swift test'),
  ];
}

// ─── CMake ───────────────────────────────────────────────────────────────────

async function detectCMake(ctx: DetectionContext): Promise<RunConfiguration[]> {
  if (!hasRootFile(ctx, 'CMakeLists.txt')) return [];
  return [
    cfg('CMake: Configure', 'cmake -B build'),
    cfg('CMake: Build', 'cmake --build build'),
  ];
}

// ─── Infrastructure: Docker Compose ──────────────────────────────────────────

async function detectDockerCompose(ctx: DetectionContext): Promise<RunConfiguration[]> {
  const composeFiles = ['docker-compose.yml', 'docker-compose.yaml', 'compose.yml', 'compose.yaml'];
  for (const filename of composeFiles) {
    if (hasRootFile(ctx, filename)) {
      return [
        cfg('Compose: Up', `docker compose -f ${filename} up`),
        cfg('Compose: Up (detached)', `docker compose -f ${filename} up -d`),
        cfg('Compose: Down', `docker compose -f ${filename} down`),
      ];
    }
  }
  return [];
}

// ─── Infrastructure: Makefile ────────────────────────────────────────────────

async function detectMakefile(ctx: DetectionContext): Promise<RunConfiguration[]> {
  for (const filename of ['Makefile', 'makefile', 'GNUmakefile']) {
    if (!hasRootFile(ctx, filename)) continue;
    const content = await readSafe(ctx, filename);
    if (!content) continue;

    const targets = content
      .split('\n')
      .filter((line) => /^[a-zA-Z_][a-zA-Z0-9_-]*\s*:/.test(line))
      .map((line) => line.split(':')[0].trim())
      .filter((t) => !t.startsWith('.') && !t.startsWith('_'));

    if (targets.length === 0) continue;
    return targets.map((target) => cfg(`make ${target}`, `make ${target}`));
  }
  return [];
}

// ─── Infrastructure: Helm ────────────────────────────────────────────────────

async function detectHelm(ctx: DetectionContext): Promise<RunConfiguration[]> {
  if (!hasRootFile(ctx, 'Chart.yaml')) return [];
  return [
    cfg('Helm: Lint', 'helm lint .'),
    cfg('Helm: Template', 'helm template .'),
  ];
}
